import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Project } from "../api/types";
import { Icon } from "./icons";
import { toast } from "./toast";
import { backdropClose } from "../lib/backdrop";

interface TransferResult {
  mode: "copy" | "move";
  target_name: string;
  count: number;
  copied: string[];
  skipped: { filename: string; reason: string }[];
}

/**
 * Move or copy selected audio files into another of the user's projects. The
 * cached transcript travels with each file (so the target doesn't re-pay/re-run
 * transcription); proofing results travel only if opted in (valid only when the
 * target shares the same manuscript). Used for both a single row and a bulk set.
 */
export function TransferAudioDialog({
  projectId,
  files,
  mode: initialMode,
  anyProofed,
  onClose,
  onDone,
}: {
  projectId: string;
  files: string[];
  mode: "copy" | "move";
  anyProofed: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"copy" | "move">(initialMode);
  const [target, setTarget] = useState("");
  const [includeProofing, setIncludeProofing] = useState(false);

  const { data: projects } = useQuery({
    queryKey: ["projects", "active"],
    queryFn: () => api<{ projects: Project[] }>("/api/projects").then((r) => r.projects),
  });
  const targets = (projects || []).filter((p) => p.id !== projectId && !p.archived);

  const run = useMutation({
    mutationFn: () =>
      api<TransferResult>(`/api/projects/${projectId}/audio/transfer`, {
        method: "POST",
        body: JSON.stringify({
          filenames: files,
          target_project_id: target,
          mode,
          include_proofing: anyProofed && includeProofing,
        }),
      }),
    onSuccess: (r) => {
      const verb = r.mode === "move" ? "Moved" : "Copied";
      const skip = r.skipped.length ? ` · ${r.skipped.length} skipped (${r.skipped[0].reason})` : "";
      if (r.count > 0) toast(`${verb} ${r.count} file${r.count > 1 ? "s" : ""} to “${r.target_name}”${skip}`, "success");
      else toast(`Nothing transferred${skip || " — files already in the target?"}`, "info");
      qc.invalidateQueries({ queryKey: ["audio", projectId] });
      qc.invalidateQueries({ queryKey: ["audio", target] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["project", target] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      onDone();
      onClose();
    },
    onError: (e: any) => toast(e.message || "Couldn't transfer the files.", "error"),
  });

  const n = files.length;
  return (
    <div className="modal-overlay" {...backdropClose(onClose)}>
      <div className="modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>{mode === "move" ? "Move" : "Copy"} {n} audio file{n > 1 ? "s" : ""}</h3>
        <div className="modal-body">
          <div className="seg" role="group" aria-label="Copy or move" style={{ marginBottom: 14 }}>
            <button className={mode === "copy" ? "on" : ""} onClick={() => setMode("copy")} aria-pressed={mode === "copy"}>
              <Icon name="copy" size={14} /> Copy
            </button>
            <button className={mode === "move" ? "on" : ""} onClick={() => setMode("move")} aria-pressed={mode === "move"}>
              <Icon name="transfer" size={14} /> Move
            </button>
          </div>

          <label className="field" style={{ marginBottom: 12 }}>
            <span className="label">Destination project</span>
            <select className="input" value={target} onChange={(e) => setTarget(e.target.value)} autoFocus>
              <option value="">Choose a project…</option>
              {targets.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {targets.length === 0 && (
              <span className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>
                You don't have another project to {mode} into — create one first.
              </span>
            )}
          </label>

          <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            The transcription travels with each file, so the destination won't re-transcribe.
            {mode === "move" && " The originals are removed from this project."}
          </div>

          {anyProofed && (
            <label className="row" style={{ gap: 8, marginTop: 12, alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" checked={includeProofing} onChange={(e) => setIncludeProofing(e.target.checked)} style={{ marginTop: 2 }} />
              <span style={{ fontSize: 13 }}>
                Also bring the proofing results
                <span className="muted" style={{ display: "block", fontSize: 12 }}>
                  Carries the proofed chapter + its flags. Only correct if the destination uses the
                  same manuscript and page layout — otherwise the flags point at the wrong text.
                </span>
              </span>
            </label>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={run.isPending}>Cancel</button>
          <button
            className="btn primary"
            disabled={!target || run.isPending || n === 0}
            onClick={() => run.mutate()}
          >
            {run.isPending ? "Working…" : `${mode === "move" ? "Move" : "Copy"} ${n} file${n > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
